import { DatePipe } from '@angular/common';
import { Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ApplicantDto, ApplicantStatusOption } from '../../../shared/services/applicants.service';
import { CountriesService } from '../../../shared/services/countries.service';
import { TenantApplicantsService } from '../../../shared/services/tenant-applicants.service';
import { TenantUsersService } from '../../../shared/services/tenant-users.service';
import { LeadMessagePreviewComponent } from '../../../shared/components/lead-message-preview/lead-message-preview.component';
import { CrmListFiltersDropdownComponent } from '../../../shared/components/crm-list-filters-dropdown/crm-list-filters-dropdown.component';
import type { CrmListFilterOption } from '../../../shared/components/crm-list-filters-dropdown/crm-list-filters-dropdown.model';
import { displayContactEmail } from '../../../shared/utils/contact-display.util';
import {
  AdminButtonComponent,
  AdminEmptyStateComponent,
  AdminTableComponent,
} from '../../../shared/admin';

const ASSIGNED_TO_ME_STORAGE_KEY = 'careergrad.applicants.filter.assignedToMe.tenant';
const SHOW_DISQUALIFIED_STORAGE_KEY = 'careergrad.applicants.filter.showDisqualified.tenant';

@Component({
  selector: 'app-tenant-applicants-page',
  standalone: true,
  imports: [
    DatePipe,
    FormsModule,
    RouterLink,
    AdminTableComponent,
    AdminEmptyStateComponent,
    AdminButtonComponent,
    LeadMessagePreviewComponent,
    CrmListFiltersDropdownComponent,
  ],
  templateUrl: './tenant-applicants-page.component.html',
  styleUrl: '../../../platform/pages/applicants/applicants-page.component.scss',
})
export class TenantApplicantsPageComponent implements OnInit, OnDestroy {
  private readonly applicantsService = inject(TenantApplicantsService);
  private readonly countriesService = inject(CountriesService);
  private readonly tenantUsersService = inject(TenantUsersService);
  private searchTimer: ReturnType<typeof setTimeout> | undefined;

  readonly applicants = signal<ApplicantDto[]>([]);
  readonly statuses = signal<ApplicantStatusOption[]>([]);
  readonly assignableUsers = signal<Array<{ userId: string; name: string }>>([]);
  readonly destinationOptions = signal<string[]>([]);
  readonly loading = signal(false);
  readonly total = signal(0);
  readonly page = signal(1);
  readonly pageSizeOptions = [10, 20, 50];

  readonly displayContactEmail = displayContactEmail;

  searchInput = '';
  statusId = '';
  preferredDestination = '';
  createdFrom = '';
  createdTo = '';
  showArchived = false;
  ownerUserId = '';
  showDisqualified = false;
  pageSize = 20;

  readonly hasNextPage = computed(() => this.page() * this.pageSize < this.total());
  readonly pageSummary = computed(() => {
    const total = this.total();
    if (total === 0) return '0 of 0';
    const start = (this.page() - 1) * this.pageSize + 1;
    const end = Math.min(this.page() * this.pageSize, total);
    return `${start}–${end} of ${total}`;
  });
  readonly assignedToFilterOptions = computed<CrmListFilterOption[]>(() => [
    { value: 'me', label: 'Assigned to me' },
    ...this.assignableUsers().map((u) => ({ value: u.userId, label: u.name })),
  ]);
  readonly statusFilterOptions = computed<CrmListFilterOption[]>(() =>
    this.statuses().map((s) => ({ value: s._id, label: s.name })),
  );
  readonly destinationFilterOptions = computed<CrmListFilterOption[]>(() =>
    this.destinationOptions().map((d) => ({ value: d, label: d })),
  );

  ngOnInit(): void {
    this.ownerUserId = this.readAssignedToMePreference() ? 'me' : '';
    this.showDisqualified = this.readShowDisqualifiedPreference();
    this.loadDestinationOptions();
    this.loadAssignableUsers();
    this.applicantsService.listStatuses().subscribe({
      next: (items) => this.statuses.set(items),
    });
    this.refresh();
  }

  ngOnDestroy(): void {
    if (this.searchTimer) clearTimeout(this.searchTimer);
  }

  onSearchChange(): void {
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => {
      this.page.set(1);
      this.refresh();
    }, 300);
  }

  onFilterChange(): void {
    this.persistAssignedToMePreference();
    this.persistShowDisqualifiedPreference();
    this.page.set(1);
    this.refresh();
  }

  onAssignedToFilterChange(value: string): void {
    this.ownerUserId = value;
    this.onFilterChange();
  }

  onStatusFilterChange(value: string): void {
    this.statusId = value;
    this.onFilterChange();
  }

  onDestinationFilterChange(value: string): void {
    this.preferredDestination = value;
    this.onFilterChange();
  }

  onFiltersClear(): void {
    this.ownerUserId = '';
    this.statusId = '';
    this.preferredDestination = '';
    this.onFilterChange();
  }

  onAssignedToMeToggle(checked: boolean): void {
    this.ownerUserId = checked ? 'me' : '';
    this.onFilterChange();
  }

  goToPage(next: number): void {
    this.page.set(next);
    this.refresh();
  }

  refresh(): void {
    this.loading.set(true);
    this.applicantsService
      .list({
        page: this.page(),
        pageSize: this.pageSize,
        search: this.searchInput,
        statusId: this.statusId || undefined,
        preferredDestination: this.preferredDestination || undefined,
        createdFrom: this.createdFrom || undefined,
        createdTo: this.createdTo || undefined,
        archiveFilter: this.showArchived ? 'archived' : 'active',
        ownerUserId: this.ownerUserId || undefined,
        showDisqualified: this.showDisqualified || undefined,
      })
      .subscribe({
        next: (result) => {
          this.applicants.set(result.items);
          this.total.set(result.total);
          this.page.set(result.page);
          this.loading.set(false);
        },
        error: () => {
          this.applicants.set([]);
          this.total.set(0);
          this.loading.set(false);
        },
      });
  }

  private loadDestinationOptions(): void {
    this.countriesService.listPublicCountries().subscribe({
      next: (list) => {
        const names = list.map((c) => c.name?.trim()).filter((v): v is string => Boolean(v));
        this.destinationOptions.set(Array.from(new Set(names)).sort((a, b) => a.localeCompare(b)));
      },
      error: () => this.destinationOptions.set([]),
    });
  }

  private loadAssignableUsers(): void {
    this.tenantUsersService.list().subscribe({
      next: (users) => {
        this.assignableUsers.set(
          users
            .filter((u) => u.isActive)
            .map((u) => ({
              userId: u.userId,
              name: [u.firstName, u.lastName].filter(Boolean).join(' ').trim() || u.email,
            }))
            .sort((a, b) => a.name.localeCompare(b.name)),
        );
      },
      error: () => this.assignableUsers.set([]),
    });
  }

  private readAssignedToMePreference(): boolean {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
      return false;
    }
    return localStorage.getItem(ASSIGNED_TO_ME_STORAGE_KEY) === 'true';
  }

  private persistAssignedToMePreference(): void {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
      return;
    }
    localStorage.setItem(ASSIGNED_TO_ME_STORAGE_KEY, String(this.ownerUserId === 'me'));
  }

  private readShowDisqualifiedPreference(): boolean {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
      return false;
    }
    return localStorage.getItem(SHOW_DISQUALIFIED_STORAGE_KEY) === 'true';
  }

  private persistShowDisqualifiedPreference(): void {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
      return;
    }
    localStorage.setItem(SHOW_DISQUALIFIED_STORAGE_KEY, String(this.showDisqualified));
  }
}
